import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link } from "@tanstack/react-router";
import { Button, IconArrowLeft, Notice, Plate, SelectField, TextField } from "@wilco/ui";
import { useState, type SubmitEvent } from "react";
import { formatDate, formatMoney, formatNumber, t, type MessageKey } from "../i18n.ts";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { parseQty } from "../stock/qty.ts";
import { purchasingApi } from "./api.ts";
import { PaymentBadge, StateBadge } from "./badges.tsx";
import { orderQuery, PURCHASING, tasksQuery } from "./queries.ts";
import type { Order, OrderLine, PriceSource } from "./types.ts";

const route = getRouteApi("/app/purchasing/orders/$id");

/** Порог утверждения с ПДВ (PUR-01). Пока из настроек сервера не приходит. */
const APPROVAL_THRESHOLD = 20_000;

/** Семейства изделий для P&L (ЗК-09). Заглушка до справочника семейств. */
const FAMILIES = ["KRH", "C13", "BPK", "ZAG"];

const SOURCE: Record<PriceSource, MessageKey> = {
  SUPPLIER: "order.source.SUPPLIER",
  COST_ESTIMATE: "order.source.COST_ESTIMATE",
  MANUAL: "order.source.MANUAL",
  INVOICE: "order.source.INVOICE",
};

/**
 * Заказ поставщику. Цены строк без ПДВ, ПДВ по строке, итог суммой строк
 * (ЗК-14). Подтверждение просит дату прибытия и семейство (ЗК-08, ЗК-09),
 * от 20 000 грн с ПДВ заказ ждёт утверждения не тем, кто подтвердил
 * (ЗК-10). После подтверждения цена переносится из рахунка (ЗК-13), оплата
 * вносится траншами со «Сплачено, грн» (ЗК-16, ЗК-17).
 */
export function OrderPage() {
  const { id } = route.useParams();
  const queryClient = useQueryClient();
  const order = useQuery(orderQuery(id));
  const tasks = useQuery(tasksQuery);
  const [notice, setNotice] = useState<NoticeState | null>(null);

  const apply = (o: Order, n?: NoticeState) => {
    queryClient.setQueryData(orderQuery(id).queryKey, o);
    void queryClient.invalidateQueries({ queryKey: PURCHASING });
    setNotice(n ?? null);
  };
  const fail = (e: unknown) => setNotice(errorNotice(e));
  const run = useMutation({ mutationFn: (fn: () => Promise<Order>) => fn() });
  const act = (fn: () => Promise<Order>, ok?: (o: Order) => NoticeState) =>
    run.mutate(fn, { onSuccess: (o) => apply(o, ok?.(o)), onError: fail });

  const o = order.data;
  if (order.error) return <Notice tone="danger" title={errorNotice(order.error).title} className="page" />;
  if (!o) return null;

  const draft = o.state === "DRAFT";
  const confirmed = o.state === "CONFIRMED";
  const foreign = o.currency !== "UAH";
  const task = tasks.data?.includes(o.id) ?? false;

  return (
    <div className="page order">
      <div className="page-head">
        <Button asChild variant="ghost" size="icon">
          <Link to="/purchasing" search={{ tab: draft || o.state === "AWAITING_APPROVAL" ? "drafts" : "expected" }} aria-label={t("order.back")}>
            <IconArrowLeft />
          </Link>
        </Button>
        <h1 className="page-title w-num">{o.number}</h1>
        <StateBadge state={o.state} />
        {confirmed ? <PaymentBadge status={o.paymentStatus} /> : null}
      </div>

      {task ? (
        <Notice tone="warning" title={t("purchasing.task.title")} className="stock-notice">
          {[o.pricesConfirmed ? null : t("purchasing.task.price"), o.paymentStatus === "PAID" ? null : t("purchasing.task.payment")].filter(Boolean).join(", ")}
        </Notice>
      ) : null}

      {o.state === "AWAITING_APPROVAL" ? (
        <Notice tone="warning" title={t("order.awaiting.title")} className="stock-notice">
          <p>
            {t("order.awaiting.body", {
              sum: formatMoney(o.grossUah, "UAH"),
              threshold: formatMoney(APPROVAL_THRESHOLD, "UAH"),
              who: o.confirmedBy ?? "",
            })}
          </p>
          <div className="notice-actions">
            <Button variant="primary" disabled={run.isPending} onClick={() => act(() => purchasingApi.approve(o.id), () => ({ tone: "success", title: t("order.approved") }))}>
              {t("order.approve")}
            </Button>
            <Button variant="ghost" disabled={run.isPending} onClick={() => act(() => purchasingApi.cancel(o.id))}>
              {t("order.cancel")}
            </Button>
          </div>
        </Notice>
      ) : null}

      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}

      <Plate cut="lg" className="order-head">
        <dl className="order-meta">
          <Meta label={t("order.supplier")} value={o.supplierName} />
          <Meta label={t("order.currency")} value={o.currency} />
          <Meta label={t("order.created")} value={formatDate(o.createdAt.slice(0, 10))} />
          {o.expectedArrival ? <Meta label={t("order.arrival")} value={formatDate(o.expectedArrival.slice(0, 10))} /> : null}
          {!draft && o.invoice ? <Meta label={t("order.invoice")} value={o.invoice} /> : null}
        </dl>
        {draft ? (
          <SelectField
            label={t("order.family")}
            value={o.family ?? ""}
            onChange={(e) => {
              // Значение сразу: запрос уйдёт позже, а контролируемый select к
              // тому времени вернётся к прежнему значению до ответа сервера.
              const family = e.target.value || null;
              act(() => purchasingApi.changeOrder(o.id, family));
            }}
            options={[{ value: "", label: "—" }, ...FAMILIES.map((f) => ({ value: f, label: f }))]}
            className="order-family"
          />
        ) : (
          <Meta label={t("order.family")} value={o.family ?? "—"} />
        )}
      </Plate>

      <section className="stock-section" aria-labelledby="order-lines">
        <h2 id="order-lines" className="desk-group-title">
          {t("order.lines")}
        </h2>
        <div className="order-lines" role="table" aria-labelledby="order-lines">
          <div className="order-line" role="row" data-head="">
            <span role="columnheader">{t("order.product")}</span>
            <span role="columnheader" className="num">
              {t("order.qty")}
            </span>
            <span role="columnheader" className="num">
              {t("order.price")}
            </span>
            <span role="columnheader" className="num">
              {t("order.vat")}
            </span>
            <span role="columnheader" className="num">
              {t("order.net")}
            </span>
          </div>
          {o.lines.map((l) => (
            // Ключ со значениями: после ответа сервера поля строки берут его числа.
            <Line key={`${String(l.no)}-${String(l.qty)}-${String(l.unitPrice)}`} order={o} line={l} busy={run.isPending} act={act} />
          ))}
        </div>
        <dl className="order-totals">
          <Total label={t("order.totalNet")} value={formatMoney(o.net, o.currency)} />
          <Total label={t("order.totalVat")} value={formatMoney(o.vat, o.currency)} />
          <Total label={t("order.totalGross")} value={formatMoney(o.gross, o.currency)} strong />
          {foreign ? (
            <Total
              label={t("order.totalUah", { rate: formatNumber(o.gross ? Math.round((o.grossUah / o.gross) * 10_000) / 10_000 : 0) })}
              value={formatMoney(o.grossUah, "UAH")}
            />
          ) : null}
        </dl>
      </section>

      {draft ? <ConfirmForm order={o} busy={run.isPending} act={act} /> : null}
      {confirmed ? <InvoiceSection order={o} busy={run.isPending} act={act} /> : null}
      {confirmed ? <Payments order={o} busy={run.isPending} act={act} /> : null}
    </div>
  );
}

type Act = (fn: () => Promise<Order>, ok?: (o: Order) => NoticeState) => void;

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="order-meta-item">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function Total({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="order-total" data-strong={strong ? "" : undefined}>
      <dt>{label}</dt>
      <dd className="w-num">{value}</dd>
    </div>
  );
}

/**
 * Строка заказа. В черновике количество и цена правятся по Enter или при
 * уходе из поля; ручная цена сохраняется при смене количества (ЗК-11).
 * У подтверждённого цена только из рахунка (ЗК-13).
 */
function Line({ order, line, busy, act }: { order: Order; line: OrderLine; busy: boolean; act: Act }) {
  const draft = order.state === "DRAFT";
  const confirmed = order.state === "CONFIRMED";
  const [qty, setQty] = useState(String(line.qty));
  const [price, setPrice] = useState(String(line.unitPrice));
  const [error, setError] = useState<string | null>(null);

  const commit = (field: "qty" | "price") => {
    const raw = field === "qty" ? qty : price;
    const n = parseQty(raw);
    const current = field === "qty" ? line.qty : line.unitPrice;
    if (n === current) return;
    if (n === null || (field === "qty" ? n <= 0 : n < 0)) {
      setError(field === "qty" ? t("shelf.qtyRequired") : t("order.priceInvalid"));
      return;
    }
    setError(null);
    if (field === "price" && confirmed) {
      act(() => purchasingApi.reprice(order.id, line.no, n), () => ({ tone: "success", title: t("order.repriced", { product: line.productName }) }));
    } else {
      act(() => purchasingApi.changeLine(order.id, line.no, { qty: field === "qty" ? n : null, unitPrice: field === "price" ? n : null }));
    }
  };

  const onKey = (field: "qty" | "price") => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit(field);
    }
  };

  return (
    <div className="order-line" role="row">
      <span role="cell" className="order-line-product">
        <span>
          <span className="w-num shelf-sku">{line.productSku}</span> {line.productName}
        </span>
        <span className="w-muted order-line-meta">
          {[line.destinationName, line.family, t(SOURCE[line.priceSource])].filter(Boolean).join(" · ")}
        </span>
        {error ? <span className="field-error">{error}</span> : null}
      </span>
      <span role="cell" className="num" data-label={t("order.qty")}>
        {draft ? (
          <TextField
            label={t("order.qtyFor", { product: line.productName })}
            hideLabel
            inputMode="decimal"
            value={qty}
            disabled={busy}
            onChange={(e) => setQty(e.target.value)}
            onBlur={() => commit("qty")}
            onKeyDown={onKey("qty")}
            className="order-input"
          />
        ) : (
          <span className="w-num">{`${formatNumber(line.qty)}\u00a0${line.uom}`}</span>
        )}
      </span>
      <span role="cell" className="num" data-label={confirmed ? t("order.invoicePrice") : t("order.price")}>
        {draft || confirmed ? (
          <TextField
            label={confirmed ? t("order.invoicePriceFor", { product: line.productName }) : t("order.priceFor", { product: line.productName })}
            hideLabel
            inputMode="decimal"
            value={price}
            disabled={busy}
            onChange={(e) => setPrice(e.target.value)}
            onBlur={() => commit("price")}
            onKeyDown={onKey("price")}
            className="order-input"
          />
        ) : (
          <span className="w-num">{formatMoney(line.unitPrice, order.currency)}</span>
        )}
      </span>
      <span role="cell" className="num w-num" data-label={t("order.vat")}>
        {line.vatRate > 0 ? `${formatNumber(line.vatRate * 100)}\u00a0%` : t("order.noVat")}
      </span>
      <span role="cell" className="num w-num" data-label={t("order.net")}>
        {formatMoney(line.net, order.currency)}
      </span>
    </div>
  );
}

/** Подтверждение: дата прибытия позже создания (ЗК-08). */
function ConfirmForm({ order, busy, act }: { order: Order; busy: boolean; act: Act }) {
  const [arrival, setArrival] = useState("");
  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    // Время из поля локальное (Киев на устройстве), сервер ждёт UTC.
    const at = arrival ? new Date(arrival).toISOString() : null;
    act(
      () => purchasingApi.confirm(order.id, at),
      (o) => ({ tone: "success", title: o.state === "AWAITING_APPROVAL" ? t("order.sentForApproval") : t("order.confirmed") }),
    );
  };
  return (
    <form className="stock-section order-confirm" onSubmit={submit}>
      <TextField label={t("order.arrival")} type="datetime-local" value={arrival} onChange={(e) => setArrival(e.target.value)} />
      <div className="order-actions">
        <Button type="submit" variant="primary" size="lg" disabled={busy}>
          {t("order.confirm")}
        </Button>
        <Button variant="ghost" disabled={busy} onClick={() => act(() => purchasingApi.cancel(order.id))}>
          {t("order.cancel")}
        </Button>
      </div>
      <p className="w-muted">{t("order.thresholdHint", { threshold: formatMoney(APPROVAL_THRESHOLD, "UAH") })}</p>
    </form>
  );
}

/** Рахунок: номер для банка (ЗК-18) и «Ціни звірено» для задачи H7. */
function InvoiceSection({ order, busy, act }: { order: Order; busy: boolean; act: Act }) {
  const [invoice, setInvoice] = useState(order.invoice ?? "");
  return (
    <section className="stock-section" aria-labelledby="order-invoice">
      <h2 id="order-invoice" className="desk-group-title">
        {t("order.invoiceTitle")}
      </h2>
      <p className="w-muted">{t("order.invoiceLead")}</p>
      <form
        className="qty-row"
        onSubmit={(e) => {
          e.preventDefault();
          act(() => purchasingApi.invoice(order.id, invoice), () => ({ tone: "success", title: t("order.invoiceSaved") }));
        }}
      >
        <TextField label={t("order.invoice")} value={invoice} onChange={(e) => setInvoice(e.target.value)} className="qty-field" />
        <Button type="submit" disabled={busy || invoice.trim() === (order.invoice ?? "")} className="qty-submit">
          {t("order.save")}
        </Button>
      </form>
      <div className="order-actions">
        {order.pricesConfirmed ? (
          <span className="order-done">{t("order.pricesConfirmed")}</span>
        ) : (
          <Button disabled={busy} onClick={() => act(() => purchasingApi.pricesConfirmed(order.id), () => ({ tone: "success", title: t("order.pricesConfirmed") }))}>
            {t("order.confirmPrices")}
          </Button>
        )}
      </div>
    </section>
  );
}

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Оплаты траншами. У гривневого заказа сумма и гривны одно число; у
 * валютного вносится и то, и другое: курс заказа это гривны всех оплат на
 * их валюту (ЗК-16), «Сплачено, грн» их сумма.
 */
function Payments({ order, busy, act }: { order: Order; busy: boolean; act: Act }) {
  const foreign = order.currency !== "UAH";
  const [date, setDate] = useState(today);
  const [amount, setAmount] = useState("");
  const [uah, setUah] = useState("");
  const [reference, setReference] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    const a = parseQty(amount);
    const u = foreign ? parseQty(uah) : a;
    if (a === null || a <= 0 || u === null || u <= 0) {
      setError(foreign ? t("payment.invalidForeign") : t("payment.invalid"));
      return;
    }
    setError(null);
    act(
      () => purchasingApi.payment(order.id, { date, amount: a, uah: u, reference: reference.trim() || null }),
      () => {
        setAmount("");
        setUah("");
        setReference("");
        return { tone: "success", title: t("payment.added", { sum: formatMoney(a, order.currency) }) };
      },
    );
  };

  return (
    <section className="stock-section" aria-labelledby="order-payments">
      <div className="section-head">
        <h2 id="order-payments" className="desk-group-title">
          {t("payment.title")}
        </h2>
      </div>
      <dl className="order-totals">
        <Total label={t("payment.paidUah")} value={formatMoney(order.paidUah, "UAH")} strong />
        {foreign && order.paidUah > 0 ? (
          <Total label={t("payment.rate")} value={formatNumber(Math.round((order.grossUah / order.gross) * 10_000) / 10_000)} />
        ) : null}
      </dl>
      {order.payments.length > 0 ? (
        <ul className="payments">
          {order.payments.map((p, i) => (
            <li key={`${p.date}-${String(i)}`} className="payment">
              <span className="w-num">{formatDate(p.date)}</span>
              <span className="payment-ref w-muted">{p.reference ?? ""}</span>
              <span className="w-num payment-sum">
                {formatMoney(p.amount, order.currency)}
                {foreign ? ` · ${formatMoney(p.uah, "UAH")}` : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="w-muted">{t("payment.none")}</p>
      )}
      {order.paymentStatus !== "PAID" ? (
        <form className="payment-form" onSubmit={submit}>
          <TextField label={t("payment.date")} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <TextField label={t("payment.amount", { currency: order.currency === "UAH" ? "грн" : order.currency })} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          {foreign ? <TextField label={t("payment.uah")} inputMode="decimal" value={uah} onChange={(e) => setUah(e.target.value)} /> : null}
          <TextField label={t("payment.reference")} value={reference} onChange={(e) => setReference(e.target.value)} placeholder={order.invoice ? t("payment.referenceHint", { invoice: order.invoice }) : undefined} />
          {error ? <span className="field-error">{error}</span> : null}
          <Button type="submit" variant="primary" disabled={busy}>
            {t("payment.add")}
          </Button>
        </form>
      ) : null}
    </section>
  );
}
